import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, ChevronDown, ChevronUp, Loader2, PackageX, TrendingUp,
} from 'lucide-react';
import {
  CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer,
  Tooltip as ChartTooltip, XAxis, YAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../../components/ui/select';
import { adminService } from '../../../../services/adminService';
import { toast } from 'sonner';
import { BANDS, SERIES_COLORS, money, pct, pts, units } from './metrics';

const KPIS = [
  { key: 'qty_ordered', label: 'Ordered', fmt: units },
  { key: 'qty_delivered', label: 'Delivered', fmt: units },
  { key: 'qty_sold', label: 'Sold', fmt: units },
  { key: 'qty_returned', label: 'Returned', fmt: units, accent: 'text-rose-600' },
  { key: 'sell_through_pct', label: 'Sell-through', fmt: pct, accent: 'text-emerald-700' },
  { key: 'return_rate_pct', label: 'Return rate', fmt: pct },
  { key: 'credit_given', label: 'Credit given', fmt: money, accent: 'text-rose-600' },
  { key: 'waste_cost', label: 'Waste cost', fmt: money, accent: 'text-rose-600' },
  { key: 'net_revenue', label: 'Net revenue', fmt: money, accent: 'text-gray-900' },
];

const COLUMNS = [
  { key: 'company_name', label: 'Client', align: 'left', fmt: (v) => v },
  { key: 'qty_ordered', label: 'Ordered', fmt: units },
  { key: 'qty_delivered', label: 'Delivered', fmt: units },
  { key: 'qty_sold', label: 'Sold', fmt: units },
  { key: 'qty_returned', label: 'Returned', fmt: units },
  { key: 'sell_through_pct', label: 'Sell-through', fmt: pct },
  { key: 'return_rate_pct', label: 'Return rate', fmt: pct },
  { key: 'over_order_rate_pct', label: 'Over-order', fmt: pct },
  { key: 'credit_given', label: 'Credit given', fmt: money },
  { key: 'waste_cost', label: 'Waste', fmt: money },
  { key: 'net_revenue', label: 'Net revenue', fmt: money },
  { key: 'contribution_margin_pct', label: 'Margin', fmt: pct },
  { key: 'order_count', label: 'Orders', fmt: units },
];

const BandChip = ({ band, testId }) => {
  const meta = BANDS[band] || BANDS.healthy;
  return (
    <Badge variant="outline" className={`${meta.chip} whitespace-nowrap`} data-testid={testId}>
      {meta.label}
    </Badge>
  );
};

const Drilldown = ({ clientId, params, onOpenScorecard }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    adminService.getClientPerformanceDrilldown(clientId, params)
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [clientId, params]);

  if (loading) {
    return (
      <div className="py-6 text-center text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading this client&apos;s detail…
      </div>
    );
  }
  if (!data) return <p className="py-4 text-sm text-gray-500">Nothing to show for this client.</p>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 py-3" data-testid={`drilldown-${clientId}`}>
      <div>
        <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">
          Most returned products
        </p>
        <div className="space-y-1">
          {(data.products || []).slice(0, 6).map((p) => (
            <div key={p.product_id} className="flex items-center justify-between text-sm gap-3">
              <span className="truncate">{p.product_name}</span>
              <span className="shrink-0 text-gray-600">
                {units(p.qty_returned)} back · {pct(p.return_rate_pct)} · {money(p.waste_cost)}
              </span>
            </div>
          ))}
          {(data.products || []).length === 0 && (
            <p className="text-sm text-gray-500">No product rows in this period.</p>
          )}
        </div>
      </div>
      <div>
        <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Sell-through by weekday</p>
        <div className="space-y-1">
          {(data.weekdays || []).map((w) => (
            <div key={w.weekday} className="flex items-center gap-3 text-sm">
              <span className="w-10 text-gray-500">{w.weekday_label}</span>
              <div className="flex-1 h-2 bg-gray-100 rounded overflow-hidden">
                <div
                  className="h-full bg-amber-400 transition-all"
                  style={{ width: `${Math.min(w.sell_through_pct || 0, 100)}%` }}
                />
              </div>
              <span className="w-28 text-right text-gray-600">
                {pct(w.sell_through_pct)} · {units(w.qty_sold)} sold
              </span>
            </div>
          ))}
          {(data.weekdays || []).length === 0 && (
            <p className="text-sm text-gray-500">No deliveries in this period.</p>
          )}
        </div>
        <Button
          variant="link"
          size="sm"
          className="px-0 mt-2"
          onClick={() => onOpenScorecard(clientId)}
          data-testid={`open-scorecard-${clientId}`}
        >
          Open full scorecard →
        </Button>
      </div>
    </div>
  );
};

export const SellThroughView = ({ params, granularity, setGranularity, onOpenScorecard }) => {
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: 'credit_given', dir: 'desc' });
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        adminService.getClientPerformanceSummary(params),
        adminService.getClientPerformanceTrend({ ...params, granularity }),
      ]);
      setSummary(s);
      setTrend(t);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load client performance');
    } finally {
      setLoading(false);
    }
  }, [params, granularity]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const list = [...(summary?.clients || [])];
    const { key, dir } = sort;
    list.sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (typeof av === 'string' || typeof bv === 'string') {
        return dir === 'asc'
          ? String(av || '').localeCompare(String(bv || ''))
          : String(bv || '').localeCompare(String(av || ''));
      }
      const an = av === null || av === undefined ? -Infinity : av;
      const bn = bv === null || bv === undefined ? -Infinity : bv;
      return dir === 'asc' ? an - bn : bn - an;
    });
    return list;
  }, [summary, sort]);

  const chartData = useMemo(() => {
    if (!trend?.periods?.length) return [];
    return trend.periods.map((period) => {
      const row = { period };
      (trend.series || []).forEach((s) => {
        const point = (s.points || []).find((p) => p.period === period);
        row[s.client_account_id] = point?.sell_through_pct ?? null;
      });
      return row;
    });
  }, [trend]);

  const toggleSort = (key) => {
    setSort((prev) => (prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'desc' }));
  };

  if (loading) {
    return (
      <div className="py-16 text-center" data-testid="sell-through-loading">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500 mx-auto" />
      </div>
    );
  }
  if (!summary) return null;

  const totals = summary.totals || {};

  return (
    <div className="space-y-5" data-testid="sell-through-view">
      {summary.segmentation?.warning && (
        <div
          className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3"
          data-testid="mixed-selection-warning"
        >
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-900">{summary.segmentation.warning}</p>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="kpi-strip">
        {KPIS.map((k) => (
          <Card key={k.key} className="border-gray-200">
            <CardContent className="p-3">
              <p className="text-xs uppercase tracking-wide text-gray-500">{k.label}</p>
              <p
                className={`text-lg font-bold mt-1 ${k.accent || 'text-gray-900'}`}
                data-testid={`kpi-${k.key}`}
              >
                {k.fmt(totals[k.key])}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {!totals.cost_recorded && (
        <p className="text-xs text-gray-500" data-testid="cost-coverage-note">
          Cost is recorded on {pct(totals.cost_coverage_pct)} of these lines — waste and margin are
          blank where no unit cost was on record.
        </p>
      )}

      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between gap-3">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-amber-500" /> Sell-through trend
          </CardTitle>
          <Select value={granularity} onValueChange={setGranularity}>
            <SelectTrigger className="h-8 w-28" data-testid="trend-granularity">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Weekly</SelectItem>
              <SelectItem value="month">Monthly</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <p className="text-sm text-gray-500 py-8 text-center">
              No deliveries in this period yet.
            </p>
          ) : (
            <div className="h-72" data-testid="trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 12, bottom: 0, left: -18 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                  <ChartTooltip formatter={(v) => (v === null ? '—' : `${v}%`)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <ReferenceLine
                    y={trend?.target_pct}
                    stroke="#059669"
                    strokeDasharray="4 4"
                    label={{ value: `Target ${trend?.target_pct}%`, fontSize: 10, fill: '#059669' }}
                  />
                  {(trend?.series || []).map((s, i) => (
                    <Line
                      key={s.client_account_id}
                      type="monotone"
                      dataKey={s.client_account_id}
                      name={s.company_name}
                      stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                      strokeWidth={2}
                      dot={{ r: 2 }}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          {trend?.series_capped && (
            <p className="text-xs text-gray-500 mt-2">
              Showing the {trend.series.length} highest-volume clients of {trend.clients_available} —
              filter to specific clients to compare others.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            Client comparison
            <span className="ml-2 text-xs font-normal text-gray-500">
              Sort by credit given to see who costs the most
            </span>
            <span
              className="block sm:hidden mt-1 text-xs font-normal text-gray-400"
              data-testid="table-scroll-hint"
            >
              Swipe the table sideways for every column
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[1100px]" data-testid="client-comparison-table">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  {COLUMNS.map((c) => (
                    <th
                      key={c.key}
                      className={`p-2 ${c.align === 'left' ? 'text-left' : 'text-right'} whitespace-nowrap`}
                    >
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 hover:text-gray-900"
                        onClick={() => toggleSort(c.key)}
                        data-testid={`sort-${c.key}`}
                      >
                        {c.label}
                        {sort.key === c.key && (
                          sort.dir === 'asc'
                            ? <ChevronUp className="h-3 w-3" />
                            : <ChevronDown className="h-3 w-3" />
                        )}
                      </button>
                    </th>
                  ))}
                  <th className="p-2 text-right whitespace-nowrap">Δ sell-through</th>
                  <th className="p-2 text-right whitespace-nowrap">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={COLUMNS.length + 2} className="p-8 text-center text-gray-500">
                      No delivered history in this period.
                    </td>
                  </tr>
                )}
                {rows.map((r) => (
                  <React.Fragment key={r.client_account_id}>
                    <tr
                      className="border-t hover:bg-amber-50/50 cursor-pointer transition-colors"
                      onClick={() => setExpanded(
                        expanded === r.client_account_id ? null : r.client_account_id,
                      )}
                      data-testid={`client-row-${r.client_account_id}`}
                    >
                      {COLUMNS.map((c) => (
                        <td
                          key={c.key}
                          className={`p-2 ${c.align === 'left' ? 'text-left' : 'text-right'} whitespace-nowrap`}
                        >
                          {c.key === 'company_name' ? (
                            <span className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">{r.company_name}</span>
                              <Badge
                                variant="outline"
                                className={r.has_guaranteed_returns
                                  ? 'text-emerald-700 border-emerald-200 text-[10px]'
                                  : 'text-gray-600 text-[10px]'}
                              >
                                {r.has_guaranteed_returns ? 'GR' : 'Trad'}
                              </Badge>
                            </span>
                          ) : c.fmt(r[c.key])}
                        </td>
                      ))}
                      <td className="p-2 text-right whitespace-nowrap">
                        {r.change?.sell_through_pts === null || r.change?.sell_through_pts === undefined ? (
                          <span className="text-gray-400">—</span>
                        ) : (
                          <span className={r.change.sell_through_pts >= 0 ? 'text-emerald-700' : 'text-rose-600'}>
                            {pts(r.change.sell_through_pts)}
                          </span>
                        )}
                      </td>
                      <td className="p-2 text-right">
                        <BandChip band={r.status_band} testId={`band-${r.client_account_id}`} />
                      </td>
                    </tr>
                    {expanded === r.client_account_id && (
                      <tr className="bg-gray-50/70">
                        <td colSpan={COLUMNS.length + 2} className="px-4">
                          <p className="text-xs text-gray-600 pt-3">
                            {r.band_reasons?.join(' · ')}
                          </p>
                          <Drilldown
                            clientId={r.client_account_id}
                            params={params}
                            onOpenScorecard={onOpenScorecard}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {summary.failed_deliveries?.count > 0 && (
        <Card className="border-rose-200">
          <CardContent className="p-4 flex items-center gap-3">
            <PackageX className="h-5 w-5 text-rose-600 shrink-0" />
            <p className="text-sm text-gray-700" data-testid="failed-delivery-note">
              {summary.failed_deliveries.count} delivery
              {summary.failed_deliveries.count === 1 ? '' : 'ies'} in this period never made it —
              {' '}{money(summary.failed_deliveries.waste_cost)} of client-caused waste is included
              above. Re-delivered orders are excluded.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SellThroughView;
